import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BrandLogo } from "@/components/BrandLogo";
import { LiveStats } from "@/components/LiveStats";
import { ExamplePhoto } from "@/components/ExamplePhoto";
import { ContactEmail } from "@/components/ContactEmail";
import { Eyebrow, Example, MONT, InventoryMock, MovePlanMock, AccessMock } from "@/pages/Movers";
import {
  Truck, ArrowRight, Video, Gavel, MapPin, Bell, CalendarRange, Map as MapIcon, Star, Box, PlayCircle,
  Sparkles, Trophy, Building2, Search, CheckCircle2, Ruler,
} from "lucide-react";

const JOIN_URL = "/auth?mode=signup&role=removalist";

function JoinButton({ testid, className = "" }) {
  const navigate = useNavigate();
  return (
    <Button size="lg" onClick={() => navigate(JOIN_URL)} data-testid={testid}
      className={`bg-kinetic hover:bg-kinetic-hover text-white gap-2 h-12 px-7 text-base font-bold shadow-lg shadow-kinetic/30 hover:-translate-y-0.5 transition-transform ${className}`}>
      <Truck className="h-5 w-5" /> Start winning jobs
    </Button>
  );
}

function JobCardMock() {
  return (
    <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }} data-testid="rem-hero-mock">
      <div className="absolute -inset-4 bg-kinetic/15 rounded-[2rem] blur-3xl" />
      <div className="relative rounded-2xl border border-border bg-card shadow-2xl p-5 sm:p-6 space-y-4">
        <Example />
        <div>
          <p className="font-mono text-[10px] uppercase text-emerald-600">New job near you</p>
          <p className="font-bold text-lg" style={MONT}>3-bedroom house move</p>
          <p className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-kinetic" /> Paddington → Manly · 14 km</p>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          {[["24.6 m³", "AI volume"], ["5 rooms", "3 videos"], ["14–16 Nov", "Flexible"]].map(([v, l]) => (
            <div key={l} className="rounded-xl bg-muted/60 p-3"><p className="font-extrabold" style={MONT}>{v}</p><p className="text-[10px] uppercase text-muted-foreground">{l}</p></div>
          ))}
        </div>
        <div className="rounded-xl border border-border p-3 space-y-2">
          <p className="font-mono text-[10px] uppercase text-muted-foreground">Your bid</p>
          {[["Sat 14 Nov", "$1,450"], ["Sun 15 Nov", "$1,390"], ["Mon 16 Nov", "$1,290"]].map(([d, p], i) => (
            <div key={d} className={`flex items-center justify-between text-sm rounded-lg px-3 py-2 animate-fade-up ${i === 2 ? "bg-kinetic-light border border-kinetic/40" : "bg-muted/40"}`} style={{ animationDelay: `${300 + i * 150}ms` }}>
              <span className="flex items-center gap-1.5"><CalendarRange className="h-3.5 w-3.5 text-kinetic" />{d}</span><b>{p}</b>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Hero() {
  const navigate = useNavigate();
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-14 pb-20 grid lg:grid-cols-2 gap-14 items-center">
      <div className="animate-fade-up">
        <div className="inline-flex items-center gap-2 rounded-full border border-kinetic/30 bg-kinetic-light px-3 py-1 mb-6">
          <Truck className="h-4 w-4 text-kinetic" /><span className="text-xs font-bold uppercase tracking-wider text-kinetic" style={MONT}>For removalists</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-brandgrey" style={MONT} data-testid="rem-hero-heading">
          Quote faster. <span className="text-kinetic">Win more moves.</span>
        </h1>
        <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
          Customers post their move with an AI-built, room-by-room inventory and cubic volume. You see exactly what's moving before you quote — no site visit, no guesswork — then bid on the days that suit your crew.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <JoinButton testid="rem-hero-join-btn" />
          <button type="button" onClick={() => document.getElementById("inventory")?.scrollIntoView({ behavior: "smooth" })} data-testid="rem-see-inventory-btn"
            className="text-sm font-semibold text-brandgrey hover:text-kinetic transition-colors flex items-center gap-1">See the inventory tools <ArrowRight className="h-4 w-4" /></button>
        </div>
        <button type="button" onClick={() => navigate("/auth")} className="mt-5 text-sm text-muted-foreground hover:text-kinetic" data-testid="rem-login-link">Already on MoveTender? Log in</button>
      </div>
      <JobCardMock />
    </section>
  );
}

function InventorySpotlight() {
  const points = [
    [Video, "Room-by-room videos", "Watch the customer's walkthrough of every room before you bid."],
    [Ruler, "Standardised m³", "Every item is mapped to a standard volume, plus a 15% packing buffer, so you can size the truck and crew."],
    [Box, "Item tags & notes", "Fragile, heavy and disassembly flags on each item, with the customer's own descriptions."],
    [MapPin, "Access at both ends", "Notes, photos and videos of stairs, lifts, parking and driveways at pickup and drop-off."],
  ];
  return (
    <section id="inventory" className="bg-asphalt text-white py-20" data-testid="rem-inventory-spotlight">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <div className="h-11 w-11 rounded-xl bg-kinetic flex items-center justify-center mb-5"><Sparkles className="h-5 w-5" /></div>
          <p className="font-mono text-xs uppercase tracking-widest text-kinetic mb-3">AI video inventory</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-5" style={MONT}>Quote from a real inventory, not a guess.</h2>
          <p className="text-white/70 leading-relaxed mb-8 max-w-lg">Customers film each room and our AI lists the furniture, boxes and appliances it sees. They then check and correct it before posting, so the job you quote on is the job you'll move.</p>
          <div className="grid sm:grid-cols-2 gap-5">
            {points.map(([Icon, t, d]) => (
              <div key={t} className="flex gap-3" data-testid={`rem-inv-point-${t.split(" ")[0].toLowerCase()}`}>
                <Icon className="h-5 w-5 text-kinetic shrink-0 mt-0.5" />
                <div><p className="font-bold text-sm mb-1" style={MONT}>{t}</p><p className="text-sm text-white/60 leading-relaxed">{d}</p></div>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-4 text-foreground">
          <InventoryMock />
          <div className="relative rounded-2xl border border-white/10 bg-white/5 p-4 flex items-center gap-4">
            <div className="h-16 w-24 rounded-lg bg-black flex items-center justify-center"><PlayCircle className="h-8 w-8 text-white" /></div>
            <div><p className="font-semibold text-white text-sm">Master bedroom · 0:48</p><p className="text-xs text-white/60">Room video attached to the job</p></div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Steps() {
  const steps = [
    [Search, "Find jobs", "Browse open jobs in your service areas as a list, on a map or on a calendar, and get alerts when new ones are posted."],
    [Gavel, "Quote with confidence", "Review the inventory, m³, videos and access details, then bid one price or a different price for each day you're free."],
    [Trophy, "Win the work", "When a customer accepts your bid you get their contact details, room-by-room unload plan, floor plan and a full inventory PDF."],
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="rem-steps">
      <Eyebrow>How it works</Eyebrow>
      <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-brandgrey mb-12 max-w-2xl" style={MONT}>From new lead to booked move in three steps.</h2>
      <div className="grid md:grid-cols-3 gap-6">
        {steps.map(([Icon, t, d], i) => (
          <div key={t} className="relative rounded-2xl border border-border bg-card p-7 hover:border-kinetic hover:-translate-y-1 transition-transform" data-testid={`rem-step-${i + 1}`}>
            <span className="absolute top-5 right-6 text-5xl font-black text-kinetic/15" style={MONT}>0{i + 1}</span>
            <div className="h-12 w-12 rounded-xl bg-kinetic-light flex items-center justify-center mb-5"><Icon className="h-6 w-6 text-kinetic" /></div>
            <h3 className="font-bold text-lg mb-2" style={MONT}>{t}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function FindJobsMock() {
  return (
    <div className="relative rounded-2xl border border-border bg-card p-5 shadow-xl space-y-3">
      <Example />
      <div className="flex gap-2">{[["List", false], ["Map", true], ["Calendar", false]].map(([t, on]) => <span key={t} className={`text-xs px-3 py-1.5 rounded-md border ${on ? "bg-asphalt text-white border-asphalt" : "border-border"}`}>{t}</span>)}</div>
      <div className="relative h-44 rounded-xl border border-border overflow-hidden bg-slate-200">
        <ExamplePhoto name="jobs-map" caption="Open jobs shown on a map of your service area" className="absolute inset-0 h-full w-full" />
        <div className="absolute inset-0 bg-white/25 pointer-events-none" />
        {[["20%", "30%"], ["55%", "45%"], ["72%", "22%"], ["38%", "70%"]].map(([l, t], i) => (
          <span key={i} className="absolute pointer-events-none h-7 w-7 -ml-3.5 -mt-3.5 rounded-full bg-kinetic text-white text-[10px] font-bold flex items-center justify-center shadow-lg animate-fade-up" style={{ left: l, top: t, animationDelay: `${i * 120}ms` }}>{[18, 9, 24, 12][i]}</span>
        ))}
        <span className="absolute bottom-2 left-2 pointer-events-none text-[10px] font-mono uppercase rounded bg-white/90 px-1.5 py-0.5 text-foreground">m³ per job</span>
      </div>
      <div className="flex items-center gap-2 text-sm rounded-lg bg-kinetic-light p-2.5"><Bell className="h-4 w-4 text-kinetic" /> New job posted in your service area</div>
    </div>
  );
}

const FEATURES = [
  { id: "find", icon: MapIcon, eyebrow: "Find jobs", title: "Leads in your area, your way.",
    body: "Set your service areas and see open jobs as a list, on a map or on a calendar of move dates. Instant or daily email alerts mean you hear about new jobs first.", Mock: FindJobsMock },
  { id: "access", icon: MapPin, eyebrow: "Know before you go", title: "Stairs, lifts and parking — sorted upfront.",
    body: "Customers add notes, photos and videos of access at both ends, so you can price for walk-ups, long carries and loading zones before you commit.", Mock: AccessMock },
  { id: "plan", icon: CheckCircle2, eyebrow: "Win the job", title: "Everything you need for moving day.",
    body: "Once your bid is accepted you get the customer's contact details, where each room should be unloaded, their new-home floor plan and a branded inventory PDF for your crew.", Mock: MovePlanMock },
];

function Features() {
  return (
    <section className="bg-muted/40 border-y border-border py-20" data-testid="rem-features">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-24">
        {FEATURES.map(({ id, icon: Icon, eyebrow, title, body, Mock }, i) => (
          <div key={id} className="grid lg:grid-cols-2 gap-12 items-center" data-testid={`rem-feature-${id}`}>
            <div className={i % 2 ? "lg:order-2" : ""}>
              <div className="h-11 w-11 rounded-xl bg-kinetic text-white flex items-center justify-center mb-5"><Icon className="h-5 w-5" /></div>
              <Eyebrow>{eyebrow}</Eyebrow>
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-brandgrey mb-4" style={MONT}>{title}</h2>
              <p className="text-muted-foreground leading-relaxed max-w-lg">{body}</p>
            </div>
            <div className={i % 2 ? "lg:order-1" : ""}><Mock /></div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Perks() {
  const items = [
    [CalendarRange, "Bid on multiple days", "Offer every day you're free in the customer's window, with a different price per day if you like."],
    [Building2, "Business profile", "Show your logo, website, insurance cover and service areas to every customer you bid for."],
    [Star, "Ratings & reviews", "Customers rate completed moves, building your reputation on the platform."],
    [Bell, "Instant notifications", "In-app and email alerts for new jobs and when your bid is accepted."],
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="rem-perks">
      <Eyebrow>Why MoveTender</Eyebrow>
      <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-brandgrey mb-12" style={MONT}>Built around how removalists work.</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {items.map(([Icon, t, d]) => (
          <div key={t} className="rounded-2xl border border-border p-6 hover:border-kinetic transition-colors">
            <Icon className="h-6 w-6 text-kinetic mb-4" />
            <h3 className="font-bold mb-1" style={MONT}>{t}</h3><p className="text-sm text-muted-foreground leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

const FAQS = [
  ["How accurate is the AI inventory?", "The AI gives a first pass from the customer's room videos, then the customer reviews it — adjusting quantities, adding missed items and tagging fragile, heavy or disassembly items — before the job is posted."],
  ["What does the m³ figure include?", "Each item is mapped to a standard volume (e.g. a 3-seater sofa is about 1.6 m³), and the total includes a 15% packing buffer."],
  ["Can I offer more than one day?", "Yes. If the customer is flexible you can select several days in their window and set one price for all of them or a different price for each day. The customer picks the day when they accept."],
  ["When do I get the customer's contact details?", "As soon as the customer accepts your bid, along with their unload plan, floor plan and the full inventory PDF."],
  ["Which jobs will I see?", "Open jobs on the platform, which you can filter to your service areas and view on a map or calendar."],
];

function Faq() {
  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="rem-faq">
      <Eyebrow>FAQ</Eyebrow>
      <h2 className="text-3xl font-extrabold tracking-tight text-brandgrey mb-8" style={MONT}>Questions from removalists.</h2>
      <Accordion type="single" collapsible>
        {FAQS.map(([q, a], i) => (
          <AccordionItem key={q} value={`q${i}`}>
            <AccordionTrigger className="text-left font-semibold" data-testid={`rem-faq-q-${i}`}>{q}</AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

export default function ForRemovalists() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background" data-testid="removalists-landing-page">
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 sm:h-28 flex items-center justify-between gap-3">
        <button type="button" onClick={() => navigate("/")} data-testid="rem-brand-link"><BrandLogo size="lg" /></button>
        <div className="flex items-center gap-1 sm:gap-2">
          <Button variant="ghost" onClick={() => navigate("/auth")} data-testid="rem-header-login-btn" className="px-2 sm:px-4">Log in</Button>
          <Button onClick={() => navigate(JOIN_URL)} data-testid="rem-header-join-btn" className="bg-kinetic hover:bg-kinetic-hover text-white px-3 sm:px-4">Join</Button>
        </div>
      </header>
      <Hero />
      <LiveStats />
      <InventorySpotlight />
      <Steps />
      <Features />
      <Perks />
      <Faq />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="relative overflow-hidden rounded-3xl bg-kinetic text-white p-10 sm:p-14 flex flex-col lg:flex-row lg:items-center justify-between gap-8" data-testid="rem-final-cta">
          <img src="/logo-mark.png" alt="" className="absolute -right-10 -bottom-10 w-64 opacity-15 pointer-events-none" />
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={MONT}>Ready for your next job?</h2>
            <p className="text-white/80 mt-2 max-w-md">Create your removalist profile in minutes and start quoting on itemised moves near you.</p>
          </div>
          <JoinButton testid="rem-final-join-btn" className="relative bg-asphalt hover:bg-asphalt-hover shadow-asphalt/30" />
        </div>
      </section>
      <footer className="border-t border-border py-8 text-center text-xs text-muted-foreground">MoveTender — Australia's Moving Marketplace · <button type="button" onClick={() => navigate("/movers")} className="hover:text-kinetic" data-testid="rem-footer-movers-link">Need a removalist?</button> · <ContactEmail /></footer>
    </div>
  );
}
